/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useMemo, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import { Trans, useTranslation } from 'react-i18next';
import { Agreement, FlowBlockAgreement } from '@staffino/model';
import { styled, Box, Paragraph, Link } from '@staffino/primitive-controls';
import ControlledCheckbox from './controls/controlled-checkbox';
import { ScrollAnchor, useScrollEffect } from './scroll';

type Props = {
  block: FlowBlockAgreement;
  agreement: Agreement;
};

const StyledReactMarkdown = styled(ReactMarkdown as any)({
  fontSize: '16px', // fixed to px, so it does not get bigger on tablet
  lineHeight: 1.5,
  whiteSpace: 'normal',
  '& > p:first-child': {
    marginTop: 0,
  },
  '& > p:last-child': {
    marginBottom: 0,
  },
});

const AgreementBlock = ({ block, agreement }: Props) => {
  const { t } = useTranslation();
  const form = useFormContext();
  const Component = form ? Controller : ControlledCheckbox;
  const invalid = !!form?.errors[block.id];
  const checkbox =
    agreement?.type === 'legal' || agreement?.type === 'additional';

  const text = useMemo(() => {
    return agreement.content;
  }, [agreement, t]);
  const checkboxRef = useRef(null);

  const { scrollToError } = useScrollEffect(
    block.id,
    form?.watch(`${block.id}`),
  );

  const required =
    agreement?.type === 'legal' ||
    agreement?.type === 'legal-informative' ||
    block.required;

  const validate = (value) => {
    const required = form?.getValues(`${block.id}_required`);
    return !(required && !value);
  };

  if (!agreement) {
    return null;
  }
  return (
    <>
      <ScrollAnchor id={block.id} />
      <Box width={1} maxWidth="30rem" m="1rem auto">
        {invalid && (
          <Paragraph
            color="notification"
            flexDirection={'row' as const}
            justifyContent="center"
            alignItems="center"
            mb="0.625rem"
          >
            <Trans>You need to agree to this agreement.</Trans>
          </Paragraph>
        )}
        {checkbox && (
          <label>
            <Component
              name={block.id}
              control={form?.control}
              as={ControlledCheckbox}
              ref={checkboxRef}
              colorStyle={agreement?.colorStyle}
              rules={{ validate }}
              onFocus={() => {
                checkboxRef.current?.focus();
                scrollToError(block.id);
              }}
            >
              <StyledReactMarkdown>{text}</StyledReactMarkdown>
              {agreement?.documentationTitle && agreement?.documentationUrl && (
                <Link href={agreement.documentationUrl}>
                  {agreement.documentationTitle}
                </Link>
              )}
            </Component>
          </label>
        )}
        {!checkbox && (
          <>
            <input ref={form?.register} type="hidden" name={block.id} />
            <StyledReactMarkdown>{text}</StyledReactMarkdown>
            {agreement?.documentationTitle && agreement?.documentationUrl && (
              <Link href={agreement.documentationUrl}>
                {agreement.documentationTitle}
              </Link>
            )}
          </>
        )}
      </Box>
      <input
        type="hidden"
        ref={form?.register}
        name={`${block.id}_required`}
        value={required?.toString()}
      />
    </>
  );
};

export default AgreementBlock;
